import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import ProtectedRoute from './components/ProtectedRoute';
import { useIsInIframe } from './hooks/useIsInIframe';
import { useIframeAuthBoot } from './hooks/useIframeAuthBoot';
import './App.css';
import './i18n';

const Layout = React.lazy(() => import('./components/Layout'));
const AuthorizeCalendar = React.lazy(() => import('./pages/AuthorizeCalendar'));
const AuthorizationError = React.lazy(() => import('./pages/AuthorizationError'));
const UpcomingMeetings = React.lazy(() => import('./pages/UpcomingMeetings'));
const Reports = React.lazy(() => import('./pages/Reports'));
const MeetingDetails = React.lazy(() => import('./pages/MeetingDetails'));
const RecordingDetails = React.lazy(() => import('./pages/RecordingDetails'));
const Settings = React.lazy(() => import('./pages/Settings'));
const MyActions = React.lazy(() => import('./pages/MyActions'));
const Series = React.lazy(() => import('./pages/Series'));
const SeriesDetail = React.lazy(() => import('./pages/SeriesDetail'));
const MeetingTemplates = React.lazy(() => import('./pages/MeetingTemplates'));
const KnowledgeGraph = React.lazy(() => import('./pages/KnowledgeGraph'));
const LiveMeeting = React.lazy(() => import('./pages/LiveMeeting'));
const ChangelogPage = React.lazy(() => import('./pages/ChangelogPage'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const NewRecording = React.lazy(() => import('./pages/NewRecording'));
const InPersonRecordingDetails = React.lazy(() => import('./pages/InPersonRecordingDetails'));
const PublicMeetingView = React.lazy(() => import('./pages/PublicMeetingView'));
const PublicRecordingView = React.lazy(() => import('./pages/PublicRecordingView'));
const PublicInPersonRecordingView = React.lazy(() => import('./pages/PublicInPersonRecordingView'));
const ErrorPage = React.lazy(() => import('./pages/ErrorPage'));
const UserNotFoundError = React.lazy(() => import('./pages/UserNotFoundError'));
const CalendarAuthSuccess = React.lazy(() => import('./pages/CalendarAuthSuccess'));
const CalendarAuthLauncher = React.lazy(() => import('./pages/CalendarAuthLauncher'));

const queryClient = new QueryClient();

function IframeTabsWrapper({ children }: { children: React.ReactNode }) {
  const isInIframe = useIsInIframe();

  if (!isInIframe) {
    return <>{children}</>;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pb-4 flex-1 min-h-0">
        {children}
      </div>
    </div>
  );
}

function AppContent() {
  return (
    <Routes>
      <Route path="/public/meeting/:linkId" element={<PublicMeetingView />} />
      <Route path="/public/recording/:linkId" element={<PublicRecordingView />} />
      <Route path="/public/in-person-recording/:linkId" element={<PublicInPersonRecordingView />} />
      <Route path="/authorize-calendar" element={<AuthorizeCalendar />} />
      <Route path="/authorization-error" element={<AuthorizationError />} />
      <Route path="/calendar-auth-success" element={<CalendarAuthSuccess />} />
      <Route path="/calendar-auth-launcher" element={<CalendarAuthLauncher />} />
      <Route path="/error" element={<ErrorPage />} />
      <Route path="/user-not-found" element={<UserNotFoundError />} />
      <Route path="/*" element={
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/meetings/past" replace />} />
            <Route path="meetings/upcoming" element={
              <IframeTabsWrapper>
                <ProtectedRoute><UpcomingMeetings /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="meetings/past" element={
              <IframeTabsWrapper>
                <ProtectedRoute><Reports /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            {/* Page transverse « Mes actions » (migration 084, GA depuis la 106).
                Le test de feature reste porté PAR LA PAGE et pas seulement par
                l'onglet de la navbar : si la clé repassait sous audience, l'URL
                directe redirigerait vers les réunions passées. */}
            <Route path="my-actions" element={
              <IframeTabsWrapper>
                <ProtectedRoute><MyActions /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="meetings/details/:id" element={
              <IframeTabsWrapper>
                <ProtectedRoute><MeetingDetails /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="recordings/:id" element={
              <IframeTabsWrapper>
                <ProtectedRoute><RecordingDetails /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="new-recording" element={
              <IframeTabsWrapper>
                <ProtectedRoute><NewRecording /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="on-demand" element={<Navigate to="/new-recording" replace />} />
            <Route path="record-in-person" element={<Navigate to="/new-recording" replace />} />
            <Route path="recordings/in-person/:id" element={
              <IframeTabsWrapper>
                <ProtectedRoute><InPersonRecordingDetails /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="settings" element={
              <IframeTabsWrapper>
                <Settings />
              </IframeTabsWrapper>
            } />
            <Route path="templates" element={
              <IframeTabsWrapper>
                <ProtectedRoute><MeetingTemplates /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="knowledge-graph" element={
              <IframeTabsWrapper>
                <ProtectedRoute><KnowledgeGraph /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            {/* Transcript temps reel (feature live_transcript, GA depuis la migration 106) */}
            <Route path="live/:botId" element={
              <IframeTabsWrapper>
                <ProtectedRoute><LiveMeeting /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="live" element={
              <IframeTabsWrapper>
                <ProtectedRoute><LiveMeeting /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="folders" element={
              <IframeTabsWrapper>
                <ProtectedRoute><Series /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="changelog" element={
              <IframeTabsWrapper>
                <ProtectedRoute><ChangelogPage /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
            <Route path="folders/:id" element={
              <IframeTabsWrapper>
                <ProtectedRoute><SeriesDetail /></ProtectedRoute>
              </IframeTabsWrapper>
            } />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      } />
    </Routes>
  );
}

function AppShell() {
  useIframeAuthBoot();
  return (
    <React.Suspense fallback={
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
      </div>
    }>
      <AppContent />
    </React.Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppShell />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
